import { Component } from 'react';
import { withAlert } from 'react-alert';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { format, isFuture } from 'date-fns';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import querystringify from 'querystringify';

import { connectWithDiscord } from '@lootboy/lib/actions/AuthActions';
import { fetchCountryIfNeeded } from '@lootboy/lib/actions/CountryActions';
import {
  fetchActiveSubscriptionIfNeeded,
  fetchSubscriptionPlansIfNeeded,
} from '@lootboy/lib/actions/SubscriptionActions';
import {
  claimMonthlyDiamondBonus,
  claimMonthlyStoreDiscount,
  fetchUserIfNeeded,
} from '@lootboy/lib/actions/UserActions';
import { getDiscordAuthCodeUrl } from '@lootboy/lib/api/auth';
import { getDateFormatUnicode } from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';
import LootBoyButton from '~/components/LootBoyButton';
import Markdown from '~/components/Markdown';
import PopupClassic from '~/components/PopupClassic';
import { DialogWithTransition } from '~/components/PopupsWithTransition';
import SubscriptionPopup from '~/components/SubscriptionPopup';

import { evalIsUserSubscribed } from '~/redux/selectors';
import { SEASON_TICKET_PATH, SEASON_TICKET_SUCCESS_PATH } from '~/routes/paths';

import ticket from '~/assets/images/currencies/1 Ticket Single.png';
import diamond from '~/assets/images/currencies/Diamond Front.png';
import logo from '~/assets/images/logo/logo-white.svg';
import lootboy from '~/assets/images/season-pass/LootBoy.png';
import discord from '~/assets/images/season-pass/subscription-summary/discord.png';

import './style.css';

class SubscriptionOverview extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    alert: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    // from mapStateToProps
    isSubscribedUser: PropTypes.bool,
    ticketBalance: PropTypes.number,
    nextTicketAwardDate: PropTypes.string,
    totalSubscriptionDiamondBonus: PropTypes.number,
    storeCreditsToClaim: PropTypes.number,
    subscriptionDiamondBonusToBeClaimed: PropTypes.number,
    subscriptionStoreDiscountsToBeClaimed: PropTypes.number,
    subscriptionValidUntil: PropTypes.string,
    planLength: PropTypes.number,
    country: PropTypes.string,
    apiUrl: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    isDiscordConnected: PropTypes.bool,
    oldVersion: PropTypes.bool,

    // from mapDispatchToProps
    fetchActiveSubscriptionIfNeeded: PropTypes.func.isRequired,
    fetchSubscriptionPlansIfNeeded: PropTypes.func.isRequired,
    fetchUserIfNeeded: PropTypes.func.isRequired,
    fetchCountryIfNeeded: PropTypes.func.isRequired,
    claimMonthlyDiamondBonus: PropTypes.func.isRequired,
    claimMonthlyStoreDiscount: PropTypes.func.isRequired,
    connectWithDiscord: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      discordUrl: null,
      renderDiscordPopupup: false,
      successDialog: false,
    };

    this.closeDialog = this.closeDialog.bind(this);
  }

  componentDidMount() {
    const { apiUrl, alert } = this.props;
    const searchString = this.props.location.search;
    const query = querystringify.parse(searchString);

    (async () => {
      try {
        const isSuccessPath =
          this.props.match.path === SEASON_TICKET_SUCCESS_PATH;
        const user = await this.props.fetchUserIfNeeded(isSuccessPath);
        if (!evalIsUserSubscribed(user)) {
          this.props.history.replace(SEASON_TICKET_PATH);
        } else if (isSuccessPath) {
          this.setState({ successDialog: true });
        }
      } catch (e) {
        // empty
      }
    })();
    this.props.fetchActiveSubscriptionIfNeeded().catch(() => {});
    this.props.fetchCountryIfNeeded().catch(() => {});
    this.props.fetchSubscriptionPlansIfNeeded().catch(() => {});

    if (query && query.code) {
      this.props
        .connectWithDiscord(query.code)
        .then(() =>
          alert.success(
            <Alert
              body={I18n.t('subscription_summary_link_success')}
              title=""
            />,
          ),
        )
        .catch((err) =>
          alert.error(
            <Alert
              body={err.message}
              title={I18n.t('common_error')}
            />,
          ),
        );
    } else if (apiUrl) {
      getDiscordAuthCodeUrl(apiUrl)
        .then((res) => this.setState({ discordUrl: res.authorizationUrl }))
        .catch(() => {});
    }

    if (query && query.error_description) {
      alert.error(
        <Alert
          body={query.error_description}
          title=""
        />,
      );
    }
  }

  closeDialog = () => this.setState({ successDialog: false });

  render() {
    const {
      ticketBalance,
      isDiscordConnected,
      country,
      totalSubscriptionDiamondBonus,
      nextTicketAwardDate,
      subscriptionValidUntil,
      planLength,
      subscriptionDiamondBonusToBeClaimed,
      subscriptionStoreDiscountsToBeClaimed,
      storeCreditsToClaim,
      alert,
      oldVersion,
    } = this.props;

    const { discordUrl, renderDiscordPopupup, successDialog } = this.state;

    const nextTicketsDate = nextTicketAwardDate || subscriptionValidUntil;
    const info = {
      1: {
        months: I18n.t('subscription_summary_1month'),
        payment: I18n.t('subscription_summary_monthly'),
        tickets: I18n.t('subscription_summary_loot_tickets', {
          monthlyTickets: 6,
        }),
        perMonth: I18n.t('subscription_summary_per_month', {
          monthlyTickets: 6,
        }),
      },
      6: {
        months: I18n.t('subscription_summary_6months'),
        payment: I18n.t('subscription_summary_semiannually'),
        tickets: I18n.t('subscription_summary_loot_tickets', {
          monthlyTickets: 7,
        }),
        perMonth: I18n.t('subscription_summary_per_month', {
          monthlyTickets: 7,
        }),
      },
      12: {
        months: I18n.t('subscription_summary_12months'),
        payment: I18n.t('subscription_summary_annually'),
        tickets: I18n.t('subscription_summary_loot_tickets', {
          monthlyTickets: 8,
        }),
        perMonth: I18n.t('subscription_summary_per_month', {
          monthlyTickets: 8,
        }),
      },
    };

    return (
      <PopupClassic
        containerClassName="subscriptionOverviewContainer"
        className="subscription-overview"
      >
        <div className="dialog-header">
          <img
            alt=""
            className="faded"
            src={lootboy}
          />
          <img
            alt="LootBoy Season Pass"
            className="logo"
            src={logo}
            title="LootBoy Season Pass"
          />
        </div>
        <div className="overview-container">
          <h1>{'Season Ticket'}</h1>
          <div className="summary-container">
            <div className="summary-column">
              <div className="section-area">
                <h2>{I18n.t('subscription_summary_tickets')}</h2>
                <div className="input-field short">
                  <img
                    alt="ticket"
                    className="icon"
                    src={ticket}
                  />
                  <h2>{ticketBalance || '--'}</h2>
                </div>
              </div>
              <div className="section-area">
                <h2>
                  {'Bonus-'}
                  {I18n.t('TabBar_tab_gems')}
                </h2>
                <div className="input-field short">
                  <img
                    alt="diamond"
                    className="icon"
                    src={diamond}
                  />
                  <h2>{subscriptionDiamondBonusToBeClaimed}</h2>
                </div>
                <div className="button-container">
                  <LootBoyButton
                    disabled={!(subscriptionDiamondBonusToBeClaimed > 0)}
                    onClick={() => {
                      this.props
                        .claimMonthlyDiamondBonus()
                        .then(() =>
                          alert.success(
                            <Alert
                              body={I18n.t(
                                'subscription_summary_diamonds_claimed',
                              )}
                              title=""
                            />,
                          ),
                        )
                        .catch((error) =>
                          alert.error(
                            <Alert
                              body={error.message}
                              title={I18n.t('common_error')}
                            />,
                          ),
                        );
                    }}
                    title={
                      subscriptionDiamondBonusToBeClaimed > 0
                        ? I18n.t('subscription_summary_claim')
                        : I18n.t('subscription_summary_already_claimed')
                    }
                    type="green"
                  />
                </div>
              </div>
            </div>
            <div className="summary-column">
              <div className="section-area">
                <h2>{I18n.t('subscription_summary_new_tickets')}</h2>
                <div className="input-field long">
                  <div className="date">
                    <h2>
                      {nextTicketsDate
                        ? format(
                            new Date(nextTicketsDate),
                            getDateFormatUnicode(country),
                          )
                        : '--'}
                    </h2>
                  </div>
                </div>
              </div>
              <div className="section-area discord">
                <h2>{'Discord'}</h2>
                <img
                  alt="discord"
                  className="discord-icon"
                  src={discord}
                />
                <div className="input-field">
                  <b>{I18n.t('subscription_summary_link_discord')}</b>
                </div>
                <div className="button-container">
                  <LootBoyButton
                    onClick={() =>
                      this.setState({
                        renderDiscordPopupup: true,
                      })
                    }
                    title={
                      isDiscordConnected
                        ? I18n.t(
                            'subscription_summary_discord_connection_linked_button_title',
                          )
                        : I18n.t('subscription_summary_link')
                    }
                    type={isDiscordConnected ? 'green' : 'white'}
                  />
                </div>
              </div>
            </div>
          </div>
          {country === 'DE' && oldVersion && (
            <div className="section-area shop-container">
              <h2>{I18n.t('subscription_summary_imbanator')}</h2>
              <div className="button-container">
                <LootBoyButton
                  disabled={!(subscriptionStoreDiscountsToBeClaimed > 0)}
                  onClick={() => {
                    this.props
                      .claimMonthlyStoreDiscount()
                      .then(() =>
                        alert.success(
                          <Alert
                            body={I18n.t(
                              'subscription_summary_card_in_inventory',
                            )}
                            title=""
                          />,
                        ),
                      )
                      .catch((error) =>
                        alert.error(
                          <Alert
                            body={error.message}
                            title={I18n.t('common_error')}
                          />,
                        ),
                      );
                  }}
                  title={
                    subscriptionStoreDiscountsToBeClaimed > 0
                      ? I18n.t('subscription_summary_discount')
                      : I18n.t('subscription_summary_already_claimed')
                  }
                  type="green"
                />
              </div>
            </div>
          )}
          <div className="section-area shop-container">
            <h2>{I18n.t('subscription_summary_season_tickets')}</h2>
          </div>
          <div className="info-container">
            <div className="summary-column">
              {planLength && (
                <div className="section-area">
                  <h3>{info[planLength || 1].months} </h3>
                  <b>
                    {I18n.t('subscription_summary_payment')}{' '}
                    {info[planLength || 1].payment}
                  </b>
                </div>
              )}
              <div className="section-area">
                <h3>{info[planLength || 1].tickets}</h3>
                <p>{info[planLength || 1].perMonth}</p>
              </div>
              {country === 'DE' && oldVersion && (
                <div className="section-area">
                  <b>
                    {I18n.t('subscription_summary_discounts_left', {
                      codesLeft: storeCreditsToClaim,
                    })}
                  </b>
                </div>
              )}
            </div>
            <div className="summary-column">
              <div className="section-area">
                <h3>{I18n.t('subscription_summary_charge')}</h3>
                <b>
                  {(subscriptionValidUntil &&
                    format(
                      new Date(subscriptionValidUntil),
                      getDateFormatUnicode(country),
                    )) ||
                    '--'}
                </b>
              </div>
              <div className="section-area">
                <b>
                  {I18n.t('subscription_summary_diamonds_collected', {
                    diamondsClaimed: totalSubscriptionDiamondBonus,
                  })}
                </b>
              </div>
            </div>
          </div>
          <div className="section-area shop-container">
            <b>
              {I18n.t('subscription_summary_info')}
              <br />
              <Link to={SEASON_TICKET_PATH}>
                {I18n.t('subscription_summary_to_season_page')}
              </Link>
            </b>
          </div>
        </div>
        <DialogWithTransition
          show={renderDiscordPopupup}
          onClose={() => this.setState({ renderDiscordPopupup: false })}
        >
          <div className="discord-container">
            <h1>{'Link Discord'}</h1>
            <Markdown>{I18n.t('subscription_summary_discord')}</Markdown>
            <Markdown>{I18n.t('subscription_summary_discord_terms')}</Markdown>
            <LootBoyButton
              onClick={() => {
                if (discordUrl) {
                  window.open(discordUrl, '_self');
                }
              }}
              title={I18n.t('subscription_summary_link')}
              type="white"
            />
          </div>
        </DialogWithTransition>
        <SubscriptionPopup
          show={successDialog}
          onClose={this.closeDialog}
          description="season_ticket_popup_confirmation_description"
          onClick={this.closeDialog}
          buttonText="season_ticket_popup_confirmation_button"
          header="season_ticket_popup_confirmation_headline"
        />
      </PopupClassic>
    );
  }
}

const mapDispatchToProps = {
  fetchActiveSubscriptionIfNeeded,
  fetchSubscriptionPlansIfNeeded,
  fetchUserIfNeeded,
  fetchCountryIfNeeded,
  claimMonthlyDiamondBonus,
  claimMonthlyStoreDiscount,
  connectWithDiscord,
};

const mapStateToProps = (state) => {
  const user = state.auth.user.data;
  const subscription = state.subscription.data;
  const subscriptionPlan =
    subscription &&
    state.subscriptionPlans.items.find(
      (plan) => plan._id === subscription.plan,
    );
  return {
    isSubscribedUser: evalIsUserSubscribed(user),
    ticketBalance: user && user.ticketBalance,
    nextTicketAwardDate:
      subscription &&
      subscription.rewardAt &&
      subscription.rewardAt.find((date) => isFuture(new Date(date))),
    totalSubscriptionDiamondBonus:
      (user && user.totalSubscriptionDiamondBonus) || 0,
    storeCreditsToClaim:
      (user && user.subscriptionStoreDiscountsToBeClaimed) || 0,
    subscriptionDiamondBonusToBeClaimed:
      (user && user.subscriptionDiamondBonusToBeClaimed) || 0,
    subscriptionStoreDiscountsToBeClaimed:
      (user && user.subscriptionStoreDiscountsToBeClaimed) || 0,
    subscriptionValidUntil: subscription && subscription.validUntil,
    planLength: subscriptionPlan && subscriptionPlan.interval,
    country: state.auth.country && state.auth.country.value,
    apiUrl: state.auth.apiUrl,
    isDiscordConnected: !!(
      user &&
      user.discordProfile &&
      user.discordProfile.id
    ),
    oldVersion: subscription && !subscription.version,
  };
};

export default withAlert()(
  connect(mapStateToProps, mapDispatchToProps)(SubscriptionOverview),
);
